import { Box, styled } from '@mui/system';
import {
  BORDER, CONTROL_TEXT, EDIT_FONT_SIZE, ERROR, ERROR_HOVER, FOCUS_RING, HAIRLINE, HOVER_BG, MUTED, PILL_BORDER, PILL_TEXT, SANS_STACK, SUCCESS, TEXT_DARK, TEXT_SECONDARY,
} from '../tokens';

export const PageBackground = styled(Box)({
  flex: 1,
  overflowY: 'auto',
  background: '#fff',
  fontFamily: SANS_STACK,
  display: 'flex',
  justifyContent: 'center',
  padding: '32px 16px',
});

export const HeroCard = styled(Box)({
  background: '#fff',
  width: '100%',
  maxWidth: 1512,
  alignSelf: 'flex-start',
});

const inputBase = {
  boxSizing: 'border-box',
  border: `1.5px solid ${BORDER}`,
  borderRadius: 8,
  padding: '8px 12px',
  fontSize: EDIT_FONT_SIZE,
  color: TEXT_DARK,
  outline: 'none',
  fontFamily: SANS_STACK,
  background: '#fff',
  '&:focus': {
    borderColor: SUCCESS,
    boxShadow: FOCUS_RING,
  },
};

export const TextInput = styled('input')(inputBase);
export const SelectInput = styled('select')(inputBase);
export const TextArea = styled('textarea')({ ...inputBase, resize: 'vertical' });

// Make MUI Autocomplete fields read like the native edit inputs above
// (1.5px border, 8px radius, shared font size, green focus ring).
export const autocompleteSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '8px',
    background: '#fff',
    padding: '2px 8px',
    fontSize: EDIT_FONT_SIZE,
    fontFamily: SANS_STACK,
    '& fieldset': { borderColor: BORDER, borderWidth: '1.5px' },
    '&:hover fieldset': { borderColor: BORDER },
    '&.Mui-focused fieldset': { borderColor: SUCCESS, borderWidth: '1.5px' },
    '&.Mui-focused': { boxShadow: FOCUS_RING },
  },
};

export const SectionLabel = styled(Box)({
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '.08em',
  color: MUTED,
  textTransform: 'uppercase',
  marginBottom: 14,
});

export const GroupLabel = styled(Box)({
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: '.06em',
  color: MUTED,
  textTransform: 'uppercase',
  marginBottom: 8,
});

export const Pill = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 3,
  border: `1px solid ${PILL_BORDER}`,
  borderRadius: 5,
  fontSize: 11,
  fontWeight: 700,
  padding: '4px 6px',
  color: PILL_TEXT,
  lineHeight: 1,
  whiteSpace: 'nowrap',
});

const disabledButton = { opacity: 0.5, cursor: 'default' };

export const OutlinedButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '7px 14px',
  border: `1.5px solid ${BORDER}`,
  color: CONTROL_TEXT,
  background: '#fff',
  borderRadius: 6,
  fontWeight: 700,
  fontSize: '.85rem',
  cursor: 'pointer',
  fontFamily: SANS_STACK,
  '&:hover': { background: HOVER_BG },
  '&:disabled': { ...disabledButton, background: '#fff' },
});

export const PrimaryButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '7px 14px',
  border: `1.5px solid ${theme.palette.primary.main}`,
  color: '#fff',
  background: theme.palette.primary.main,
  borderRadius: 6,
  fontWeight: 700,
  fontSize: '.85rem',
  cursor: 'pointer',
  fontFamily: SANS_STACK,
  '&:hover': { background: theme.palette.primary.dark },
  '&:disabled': { ...disabledButton, background: theme.palette.primary.main },
}));

// Coral-outlined button on white, e.g. "Edit" in a hero toolbar.
export const AccentButton = styled('button')(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '7px 14px',
  border: `1.5px solid ${theme.palette.primary.main}`,
  color: theme.palette.primary.main,
  background: '#fff',
  borderRadius: 6,
  fontWeight: 700,
  fontSize: '.85rem',
  cursor: 'pointer',
  fontFamily: SANS_STACK,
  '&:hover': { background: theme.palette.primary.tint },
  '&:disabled': { ...disabledButton, background: '#fff' },
}));

// AccentButton's destructive twin, for an action that removes the entity.
export const DangerButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '7px 14px',
  border: `1.5px solid ${ERROR}`,
  color: ERROR,
  background: '#fff',
  borderRadius: 6,
  fontWeight: 700,
  fontSize: '.85rem',
  cursor: 'pointer',
  fontFamily: SANS_STACK,
  '&:hover': { borderColor: ERROR_HOVER, color: ERROR_HOVER, background: HOVER_BG },
  '&:disabled': {
    ...disabledButton, background: '#fff', borderColor: ERROR, color: ERROR,
  },
});

// The "‹ Back" link every hero page opens with.
export const BackButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  border: 'none',
  background: 'none',
  color: TEXT_SECONDARY,
  fontSize: '.82rem',
  cursor: 'pointer',
  fontFamily: SANS_STACK,
  '&:hover': { color: theme.palette.primary.main },
}));

// A small underlined text action, e.g. "clear" next to a date field.
export const LinkButton = styled('button')(({ theme }) => ({
  border: 'none',
  background: 'none',
  color: MUTED,
  fontSize: 12,
  textDecoration: 'underline',
  cursor: 'pointer',
  '&:hover': { color: theme.palette.primary.main },
}));

export const TagChip = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  border: `1px solid ${BORDER}`,
  borderRadius: 10,
  padding: '3px 10px',
  fontSize: 12,
  color: TEXT_DARK,
  background: '#fff',
  letterSpacing: '.3px',
});

// The "×" that removes a chip.
export const ChipRemove = styled('button')({
  border: 'none',
  background: 'none',
  color: MUTED,
  cursor: 'pointer',
  fontSize: 15,
  lineHeight: 1,
  padding: 0,
  marginLeft: 2,
});

export const IconToggle = styled('button')({
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  padding: 8,
  borderRadius: 6,
  display: 'inline-flex',
  alignItems: 'center',
  '&:hover': { background: HOVER_BG },
});

export const ActionDivider = styled(Box)({
  width: 1,
  height: 24,
  background: HAIRLINE,
  flexShrink: 0,
  margin: '0 2px',
});

const cardOverlayButton = {
  position: 'absolute',
  top: 6,
  width: 22,
  height: 22,
  border: 'none',
  borderRadius: '50%',
  background: 'rgba(0,0,0,.55)',
  color: '#fff',
  cursor: 'pointer',
  fontSize: 14,
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 2,
};

export const CardDeleteButton = styled('button')({ ...cardOverlayButton, right: 6 });

export const CardCornerButton = styled('button')({
  ...cardOverlayButton,
  left: 6,
  '&:disabled': { opacity: 0.45, cursor: 'default' },
});

export const cardGridSx = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fill, minmax(230px, 1fr))',
  gap: 2,
};

export const cardCellSx = {
  position: 'relative',
  aspectRatio: '1 / 1',
  containerType: 'size',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  minHeight: 0,
};

export const cardInnerSx = {
  width: 'min(100cqw, 100cqh)',
  height: 'min(100cqw, 100cqh)',
};
